import { Lock } from '@orgus/ui/icons';
import { Badge, cn, DataTable, Tooltip, formatInt, formatPercent, plural, RelativeTime, Sparkline } from '@orgus/ui';
import type { Column } from '@orgus/ui';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import { HealthBadge, ScoreText } from './HealthBadge.tsx';
import type { FleetRow } from './fleet.ts';
import type { RepoHealth } from './model.ts';
import { HEALTH_LABEL } from './model.ts';

/** The one placeholder for a missing number: a quiet dash, never a word that reads as data. */
export function NoValue({ why }: { why?: string }) {
  const dash = (
    <span tabIndex={why ? -1 : undefined} className="text-fg-subtle num">
      -{why && <span className="sr-only">{why}</span>}
    </span>
  );
  return why ? <Tooltip content={why}>{dash}</Tooltip> : dash;
}

/**
 * A score figure in a list: always fg, never status-coloured, since the row's status already
 * says when a person should look. A quiet dash before the first check or after a failed one.
 */
export function ListScore({ health, score, className }: { health: RepoHealth; score: number | undefined; className?: string }) {
  const known = health !== 'pending' && health !== 'error' && score !== undefined;
  return <ScoreText health={health} score={score} className={cn(className, known && 'text-fg')} />;
}

/** How much of the debt captured at onboarding is left, and any new violations on top. */
export function BurnDown({ remaining, total, fresh }: { remaining: number; total: number; fresh: number }) {
  const done = total ? (total - remaining) / total : 1;
  return (
    <Tooltip content={`${formatPercent(done)} of the debt captured at onboarding is gone`}>
      <span tabIndex={-1} className="flex min-w-0 items-baseline justify-end gap-1 text-caption">
        {fresh > 0 && <span className="mr-2 font-medium text-status-danger num">+{formatInt(fresh)} new</span>}
        <span className="text-ui text-fg num">{formatInt(remaining)}</span>
        <span className="text-fg-subtle num">of {formatInt(total)}</span>
      </span>
    </Tooltip>
  );
}

const columns: Column<FleetRow>[] = [
  {
    key: 'status',
    header: 'Status',
    type: 'badge',
    sortValue: (r) => r.report?.score ?? -1,
    text: (r) => r.health,
    cell: (r) => <HealthBadge health={r.health} hideLabel="below-xl" />,
  },
  {
    key: 'repo',
    header: 'Repo',
    grow: true,
    text: (r) => `${r.name} ${r.packs.join(' ')} ${r.topRules.map((t) => t.title).join(' ')}`,
    sortValue: (r) => r.name.toLowerCase(),
    cell: (r) => (
      <span className="flex min-w-0 items-center gap-3">
        <span className="shrink-0 font-medium text-fg">{r.name}</span>
        {r.packs[0] && (
          <Badge data-chip="" size="sm" variant="outline" className="hidden shrink-0 md:inline-flex" icon={r.rulebookFrom === 'hub' ? <Lock aria-label="Rulebook kept in Orgus" /> : undefined}>
            {r.packs[0]}
            {r.packs.length > 1 ? ` +${r.packs.length - 1}` : ''}
          </Badge>
        )}
        {r.topRules[0] && (
          <span data-meta="" className="hidden min-w-0 truncate text-caption text-fg-subtle lg:inline">
            {formatInt(r.topRules[0].count)} {r.topRules[0].title}
          </span>
        )}
      </span>
    ),
  },
  {
    key: 'score',
    header: 'Score',
    type: 'number',
    sortValue: (r) => (r.report && !r.report.error ? r.report.score : -1),
    cell: (r) => (
      <span className="flex items-center justify-end gap-2">
        {r.history.length > 1 && <Sparkline values={r.history.map((p) => p.score)} min={0} max={100} width={48} height={16} tone="neutral" label={`${r.name} score trend`} />}
        <ListScore health={r.health} score={r.report?.score} className="w-8 text-right" />
      </span>
    ),
  },
  {
    key: 'ratchet',
    header: 'Debt left',
    type: 'number',
    hideBelow: 'lg',
    sortValue: (r) => r.ratchet?.remaining ?? -1,
    cell: (r) => (r.ratchet ? <BurnDown {...r.ratchet} /> : <NoValue why="Not onboarded" />),
  },
  {
    key: 'waived',
    header: 'Waived',
    type: 'number',
    hideBelow: 'md',
    sortValue: (r) => r.report?.waived ?? 0,
    cell: (r) => (
      <span className="inline-flex items-baseline justify-end gap-2">
        {r.waiversExpiring > 0 && (
          <Tooltip content={`${plural(r.waiversExpiring, 'waiver')} kept in Orgus ${r.waiversExpiring === 1 ? 'runs' : 'run'} out within a week`}>
            <span tabIndex={-1} className="text-caption font-medium text-status-attention num">
              {formatInt(r.waiversExpiring)} expiring
            </span>
          </Tooltip>
        )}
        {r.report?.waived ? <span className="text-fg-muted num">{formatInt(r.report.waived)}</span> : r.waiversExpiring > 0 ? null : <NoValue />}
      </span>
    ),
  },
  {
    key: 'drift',
    header: 'Drift',
    type: 'number',
    hideBelow: 'lg',
    sortValue: (r) => r.driftTotal,
    cell: (r) => (r.drift && r.driftTotal ? <span className="text-fg-muted num">{formatInt(r.driftTotal)}</span> : <NoValue why={r.drift ? undefined : 'Not scanned yet'} />),
  },
  {
    key: 'checked',
    header: 'Checked',
    type: 'date',
    hideBelow: 'sm',
    sortValue: (r) => r.report?.checkedAt ?? 0,
    cell: (r) => (r.report ? <RelativeTime ts={r.report.checkedAt} className="text-caption text-fg-subtle" /> : <NoValue />),
  },
];

/**
 * A phone row: the status icon and the repo, the status in words and when it was checked as a
 * caption, the score in its own right-aligned column. Debt, waivers and drift wait for the repo.
 */
const phoneColumns: Column<FleetRow>[] = [
  {
    key: 'repo',
    header: 'Repo',
    grow: true,
    text: columns[1]!.text,
    sortValue: columns[1]!.sortValue,
    cell: (r) => (
      <span className="flex min-w-0 items-center gap-2">
        <HealthBadge health={r.health} hideLabel className="shrink-0" />
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-fg">{r.name}</span>
          <span className="flex min-w-0 gap-2 text-caption text-fg-subtle">
            <span className="truncate">{HEALTH_LABEL[r.health]}</span>
            {r.report && <RelativeTime ts={r.report.checkedAt} className="shrink-0" />}
          </span>
        </span>
      </span>
    ),
  },
  {
    key: 'score',
    header: 'Score',
    type: 'number',
    sortValue: columns[2]!.sortValue,
    cell: (r) => <ListScore health={r.health} score={r.report?.score} />,
  },
];

/** One row per repo across the fleet; a row opens the repo. */
export function FleetTable({ rows, onOpen }: { rows: FleetRow[]; onOpen: (projectId: string) => void }) {
  const phone = useMediaQuery(NARROW_QUERY);
  return (
    <DataTable
      label="Discipline across every repo"
      rows={rows}
      columns={phone ? phoneColumns : columns}
      rowKey={(r) => r.projectId}
      searchable={{ placeholder: 'Filter repos' }}
      onRowClick={(r) => onOpen(r.projectId)}
      rowLabel={(r) => `Open ${r.name}`}
      initialSort={{ key: 'score', dir: 'asc' }}
      pageSize={false}
    />
  );
}
